// Read a people table pasted from ChatGPT (or a spreadsheet) into rows.
//
// ChatGPT tables arrive three ways depending on how they were copied: tab-separated
// (copy from the rendered table), pipe-separated markdown ("| a | b |"), or columns
// padded with runs of spaces (tabs flattened by the clipboard). Columns are found
// from the header row by meaning, not position, so "Your category | Person |
// Current/public title | Fit" and "Name, Title, Source" both work.

export interface PastedPerson {
  full_name: string;
  title: string;
  category: string;
  notes: string;
  source_url: string;
  email: string;
  confidence: "high" | "medium" | "low";
}

type Field = "name" | "title" | "category" | "notes" | "url" | "email";

const HEADER: [Field, RegExp][] = [
  ["email", /e-?mail/i],
  ["url", /source|link|url|profile|linkedin|evidence url/i],
  ["category", /categor|group|area|function|segment|bucket/i],
  ["name", /person|name|contact|who|executive|people/i],
  ["title", /title|position|role|job|designation/i],
  ["notes", /fit|note|comment|why|relevan|confidence|evidence|status|match|remark/i],
];

function splitRow(line: string, mode: "pipe" | "tab" | "spaces"): string[] {
  if (mode === "pipe") return line.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map(c => c.trim());
  if (mode === "tab") return line.split("\t").map(c => c.trim());
  return line.trim().split(/\s{2,}/).map(c => c.trim());
}

function clean(s: string): string {
  return s.replace(/\*\*|__|`/g, "").replace(/\[([^\]]+)\]\((https?:[^)]+)\)/g, "$1 $2").replace(/\s+/g, " ").trim();
}

function confidenceOf(text: string): PastedPerson["confidence"] {
  const t = text.toLowerCase();
  if (/needs? confirm|to confirm|unverified|unclear|may |might|possibly|former|previous|past |left |not sure|uncertain|low/.test(t)) return "low";
  if (/exact|very strong|strong|confirmed|direct|decision-?maker|high/.test(t)) return "high";
  return "medium";
}

export function parsePastedTable(text: string): { people: PastedPerson[]; columns: string[] } {
  const lines = text.split(/\r?\n/).filter(l => l.trim() && !/^\s*\|?\s*:?-{3,}/.test(l));
  if (!lines.length) return { people: [], columns: [] };

  const mode: "pipe" | "tab" | "spaces" =
    lines.filter(l => (l.match(/\|/g) || []).length >= 2).length >= lines.length / 2 ? "pipe"
      : lines.filter(l => l.includes("\t")).length >= lines.length / 2 ? "tab"
        : "spaces";
  const rows = lines.map(l => splitRow(l, mode).map(clean)).filter(r => r.some(Boolean));
  if (!rows.length) return { people: [], columns: [] };

  // Header: the first row, if at least one cell names a column we know.
  const map = new Map<number, Field>();
  rows[0].forEach((cell, i) => {
    for (const [field, re] of HEADER) {
      if (re.test(cell) && ![...map.values()].includes(field)) { map.set(i, field); break; }
    }
  });
  const hasHeader = [...map.values()].includes("name");
  const body = hasHeader ? rows.slice(1) : rows;
  if (!hasHeader) {
    map.clear();
    const width = Math.max(...rows.map(r => r.length));
    const guess: Field[] = width >= 4 ? ["name", "title", "notes", "url"] : width === 3 ? ["name", "title", "notes"] : ["name", "title"];
    guess.forEach((f, i) => map.set(i, f));
  }

  const people: PastedPerson[] = [];
  for (const r of body) {
    const get = (f: Field) => {
      const i = [...map.entries()].find(([, v]) => v === f)?.[0];
      return i == null ? "" : (r[i] || "");
    };
    const all = r.join(" ");
    const name = get("name").replace(/\s*\(.*?\)\s*$/, "");
    if (!name || name.length > 80 || /^(n\/a|none|—|-|not found|tbd)$/i.test(name)) continue;
    const url = (get("url").match(/https?:\/\/\S+/) || all.match(/https?:\/\/\S+/) || [""])[0].replace(/[),.;]+$/, "");
    const email = (get("email").match(/[^\s@]+@[^\s@]+\.[a-z]{2,}/i) || all.match(/[^\s@]+@[^\s@]+\.[a-z]{2,}/i) || [""])[0];
    const notes = get("notes");
    people.push({
      full_name: name,
      title: get("title"),
      category: get("category"),
      notes,
      source_url: url,
      email,
      confidence: confidenceOf(`${notes} ${get("title")}`),
    });
  }
  const columns = [...map.entries()].sort((a, b) => a[0] - b[0]).map(([i, f]) => `${hasHeader ? rows[0][i] : `column ${i + 1}`} → ${f}`);
  return { people, columns };
}
